import { useEffect, useLayoutEffect, useRef } from 'react'
import { useAppSelector } from '@/store/hooks'
import { createAlertThrottle, type AlertThrottle } from './use-alert-throttle'
import type { AlertEvent } from './alert-types'
import type { ISoundPlayer, SoundId } from './audio/sound-player'

export interface UseAlertEffectsOptions {
  sound: ISoundPlayer
  cooldownMs?: number
  defaultSoundId: SoundId
}

/**
 * Plays the sound ping for new alerts, app-wide. Whether an alert sounds at
 * all is decided where it is created (use-alert-stream.ts checks the panel's
 * cooldown against alert-ledger.ts and marks repeats silent), so this only
 * spaces pings out: the throttle keeps a burst to one ping per `cooldownMs`.
 */
export function useAlertEffects(opts: UseAlertEffectsOptions) {
  const events = useAppSelector((s) => s.alerts.events)
  const optsRef = useRef(opts)
  useLayoutEffect(() => {
    optsRef.current = opts
  })

  const initializedRef = useRef(false)
  const lastTopIdRef = useRef<string | null>(null)
  const throttleRef = useRef<AlertThrottle | null>(null)

  useEffect(() => {
    const throttle = createAlertThrottle({
      cooldownMs: opts.cooldownMs ?? 250,
      onFire: (winner) => {
        const o = optsRef.current
        // Events carry the emitting panel's audio settings (soundId/volume);
        // fall back to the hook defaults for producers that don't stamp them.
        if (winner.playSound !== false) {
          o.sound.play(winner.soundId ?? o.defaultSoundId, winner.volume).catch(() => {})
        }
      },
    })
    throttleRef.current = throttle
    return () => throttle.dispose()
  }, [opts.cooldownMs])

  useEffect(() => {
    const throttle = throttleRef.current
    if (!throttle) return

    if (!initializedRef.current) {
      initializedRef.current = true
      lastTopIdRef.current = events[0]?.id ?? null
      return
    }

    if (events.length === 0) return
    const topId = events[0].id
    if (topId === lastTopIdRef.current) return

    let newEvents: AlertEvent[]
    const lastTopId = lastTopIdRef.current
    if (lastTopId === null) {
      newEvents = events
    } else {
      const idx = events.findIndex((e) => e.id === lastTopId)
      newEvents = idx === -1 ? events : events.slice(0, idx)
    }
    lastTopIdRef.current = topId

    // events array is newest-first; iterate in oldest→newest order so throttle queue forms naturally
    for (let i = newEvents.length - 1; i >= 0; i--) {
      const e = newEvents[i]
      if (e.playSound === false) continue
      throttle.enqueue(e)
    }
  }, [events])
}
